import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableItemProps<T> {
  id: string | number;
  item: T;
  renderItem: (item: T) => React.ReactNode;
}

function SortableItem<T>({ id, item, renderItem }: SortableItemProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id.toString() });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative',
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`bg-white flex items-center gap-3 border-t p-3 list-none ${
        isDragging ? 'shadow-lg border-primary rounded-lg ring-2 ring-primary/10' : 'border-gray-200/80'
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 px-2 hover:text-primary transition-colors flex-shrink-0"
        aria-label="Drag item"
      >
        ☰
      </button>

      <div className="flex-1 min-w-0">{renderItem(item)}</div>
    </li>
  );
}

export interface DragDropListProps<T> {
  title?: string;
  header?: React.ReactNode;
  data: T[];
  isLoading?: boolean;
  getId: (item: T) => string | number;
  renderItem: (item: T) => React.ReactNode;
  onReorder: (items: T[]) => void;
  onSave?: () => void;
  saving?: boolean;
}

const DragDropList = <T,>({
  title,
  header,
  data,
  isLoading = false,
  getId,
  renderItem,
  onReorder,
  onSave,
  saving = false,
}: DragDropListProps<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex((item) => getId(item).toString() === active.id.toString());
    const newIndex = data.findIndex((item) => getId(item).toString() === over.id.toString());

    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(data, oldIndex, newIndex));
  };

  const itemIds = React.useMemo(() => data.map(item => getId(item).toString()), [data, getId]);

  return (
    <div className="w-full py-3">
      {(title || onSave) && (
        <div className="flex justify-between items-center mb-4">
          {title && <h4 className="mb-0 text-lg font-bold text-foreground">{title}</h4>}
          {onSave && (
            <button
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 disabled:opacity-50"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Order'}
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex items-center gap-4 p-4 border rounded-lg bg-card animate-pulse">
                    <div className="w-5 h-5 bg-muted rounded" />
                    <div className="flex-1 space-y-2">
                        <div className="h-4 bg-muted rounded w-1/3" />
                        <div className="h-3 bg-muted rounded w-1/4" />
                    </div>
                    <div className="w-12 h-6 bg-muted rounded-full" />
                </div>
            ))}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-card">
          {header && (
            <div className="bg-secondary/10 border-b border-border flex items-center gap-3 p-3">
              {/* Spacer to match the drag handle width in SortableItem */}
              <div className="w-9 flex-shrink-0" /> 
              <div className="flex-1 min-w-0">
                {header}
              </div>
            </div>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={itemIds}
              strategy={verticalListSortingStrategy}
            >
              <ul className="divide-y list-none p-0 m-0">
                {data.map((item) => (
                  <SortableItem
                    key={getId(item)}
                    id={getId(item)}
                    item={item}
                    renderItem={renderItem}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
};

export default DragDropList;
